import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Download, Table2, AlertTriangle, FileText, Code, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import { FieldEditor } from './FieldEditor';
import { canonicalTransform } from '@/utils/canonical/canonicalTransform';
import { CAT } from '@/utils/canonical/CAT';
import type { BillHeader, LineItem } from '@/utils/parserTransform';

interface ExtractedDataDisplayProps {
  header: BillHeader;
  lineItems: LineItem[];
  rawData: any;
  onExportCSV: () => void;
  onFieldUpdate: (field: string, value: string) => void;
}

export function ExtractedDataDisplay({ 
  header, 
  lineItems, 
  rawData, 
  onExportCSV,
  onFieldUpdate 
}: ExtractedDataDisplayProps) {
  // Normalize raw payload node
  const dataNode = (rawData && (rawData as any).data) ? (rawData as any).data : rawData;

  // Reintroduce canonical categories for Basic, Financial, Address, Technical, and Other
  const confidence = (dataNode && (dataNode as any).confidence) ? (dataNode as any).confidence : {};

  // Confidence resolver for a given field keyPath
  const getFieldConfidence = (field: string): number => {
    for (const [key, value] of Object.entries(confidence)) {
      if (key.toLowerCase().replace(/\s+/g, '_') === field.toLowerCase()) {
        return typeof value === 'number' ? value : 0;
      }
    }
    return 1; // default high confidence
  };

  // Helper to format values for display
  const formatFieldValue = (value: any): string => {
    if (typeof value === 'number') return value % 1 !== 0 ? value.toFixed(2) : String(value);
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '';
    if (typeof value === 'object' && value !== null) return JSON.stringify(value, null, 2);
    return value == null ? '' : String(value);
  };

  const canonical = canonicalTransform(rawData);
  const mapCat = (arr: Array<{label: string; value: any; keyPath: string}>) =>
    (arr || []).map(f => ({ label: f.label, field: f.keyPath, value: f.value }));

  const fieldGroups = {
    basic: mapCat(canonical.categories[CAT.Basic] || []),
    financial: mapCat(canonical.categories[CAT.Financial] || []),
    address: mapCat(canonical.categories[CAT.Address] || []),
    technical: mapCat(canonical.categories[CAT.Technical] || []),
    other: mapCat(canonical.categories[CAT.Other] || []),
  };
  const totalFields = canonical.totalFields;

  // Discover identifiers in common shapes
  const identifiers: Array<{
    identifier?: string;
    utilityType?: string;
    periodFrom?: string;
    periodTo?: string;
    readType?: string;
    parserChargesData?: any[];
  }> = (() => {
    const candidates: any[] = [];
    const tryPaths = [
      (dataNode as any)?.results?.parserIdentifiersData,
      (dataNode as any)?.results?.data?.parserIdentifiersData,
      (dataNode as any)?.parserIdentifiersData,
      (rawData as any)?.parserIdentifiersData,
    ];
    for (const arr of tryPaths) {
      if (Array.isArray(arr)) return arr as any[];
    }
    return candidates;
  })();

  // Flatten line items from all identifiers
  const flatLineItems: Array<{
    meterNumber?: string;
    meterMultiplier?: string | number | null;
    lineItemDescription?: string;
    previousReading?: string | number | null;
    currentReading?: string | number | null;
    quantity?: string | number | null;
    quantityUOM?: string;
    rate?: string | number | null;
    rateUOM?: string;
    total?: string | number | null;
  }> = (identifiers || []).flatMap((id: any) => Array.isArray(id?.parserChargesData) ? id.parserChargesData : []);

  // Tax-only items derived from Line Items (description keyword match)
  const TAX_KEYWORDS = [
    'tax',
    'franchise cost',
    'state tax',
    'municipal tax',
    'sales tax',
    'gross receipt tax',
    'gross receipts tax',
    'state tax adjustment',
    'va electric consumption tax',
    'local consumers tax',
    'state sales tax',
    'grt & other tax surcharges',
    'school tax',
    'county tax',
    'city tax',
    'tulsa franchise fee',
    'generation municipal surcharge',
    'gross receipts tax (state tax)',
    'il state utility tax',
    'franchise fee (required local fee)',
    'franchise fee',
    'utility tax (local tax)',
    'utility tax',
    'florida sales tax (state tax)',
    'florida sales tax',
    'county sales tax (local tax)',
    'county sales tax',
    'city of los angeles utility tax',
    'city/town tax',
    'state energy surcharge',
    'icc regulatory tax',
    'franchise cost adjustment',
    'utility fund tax',
    'state gas use tax',
    'rate inc montgmry co sc tx',
    'facilities charge',
    'bay fee',
    's burl commercial local option',
    'vermont state sales tax',
    'georgia sales tax',
    'manatee county tax',
    'municipal tax el',
    'property tax recovery fee',
    'right of way toll-reno',
    'cocoa 10% public service tax on water',
    'pipeline upgrade charge (isrs) tax',
    'pipeline upgrade charge (isrs)',
    'special tax',
    'franchise tax',
    'metro supportive housing services tax recovery',
    'ft smith city tax',
    'ft smith sebastian municipal tax',
    'oregon commercial activities tax recovery',
    'sebastian county tax',
    'arkansas state sales tax',
    'maine state tax',
    'service and facility charge',
    'tax cuts jobs act-fixed',
    'tax cuts jobs act-volumetric',
    'franchise or occupational fee',
    'water tax',
    'effect of port orchard city tax',
    'anoka county transit tax',
    'coon rapids franchise fee',
    'mn state tax',
    'metro area sales tax',
    'metro area transportation tax',
    'gst', 'qst', 'hst', 'pst', 'igst', 'cgst', 'sgst', 'vat',
    'vt state tax',
    'lo/spl/ed/tsp',
    'state and local',
    'sacramento county tax',
    'citrus heights city tax',
    'metro county tax',
    'state tax surcharge',
    'penna sales tax',
    'allegheny county sales tax',
    'tax repairs surcredit',
    'current water-sewer tax',
    'state testing fee',
    'current fees',
    'ontario electricity rebate',
    'rpp settlement',
    'transit tax',
    'hennepin county tax',
    'franchise fee-powder springs',
    'tax - charges',
    'county tax dakota',
    'indiana sales tax',
    'franchise fee eagan',
    'transportation sales tax',
    'city franchise fee',
    'water excise tax',
    'excise tax',
    'municipal franchise fee',
  ];

  const normalize = (s: any) => String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');

  const isTaxLike = (desc?: string) => {
    if (!desc) return false;
    const norm = normalize(desc);
    // Generic catch for the word tax and common abbreviations
    if ((` ${norm} `).includes(' tax ')) return true;
    const abbrev = [' vat ', ' gst ', ' hst ', ' pst ', ' igst ', ' cgst ', ' sgst ', ' excise '];
    if (abbrev.some(a => (` ${norm} `).includes(a))) return true;
    // Phrase-based keywords
    return TAX_KEYWORDS.some(k => (` ${norm} `).includes(` ${normalize(k)} `));
  };
  const taxItems = (flatLineItems || []).filter((li: any) => isTaxLike(li?.lineItemDescription));

  // Simple formatting helpers
  const fmt = (v: any) => v === null || v === undefined || v === '' ? '-' : String(v);

  const navigate = useNavigate();

  const goManageSubscription = () => {
    // Direct user to enterprise upgrade page for integration features
    navigate('/dashboard/manage-subscription');
  };

  return (
    <Card className="shadow-lg border-0 bg-white min-h-[700px] flex flex-col">
      <CardHeader className="pb-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="text-base">Extracted Bill Data</CardTitle>
            {header.needs_review.length > 0 && (
              <Badge variant="secondary" className="gap-1 text-xs">
                <AlertTriangle className="h-3 w-3" />
                {header.needs_review.length} need review
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={onExportCSV} size="sm" className="h-8 gap-1.5">
              <Download className="h-3 w-3" />
              <span className="text-xs">Export CSV</span>
            </Button>
            <Button size="sm" className="h-8 gap-1.5" onClick={goManageSubscription}>
              <Link2 className="h-3 w-3" />
              <span className="text-xs">Connect to API</span>
            </Button>
            <Button size="sm" className="h-8 gap-1.5" onClick={goManageSubscription}>
              <Table2 className="h-3 w-3" />
              <span className="text-xs">Connect to Google Sheet</span>
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
          <span>Confidence: 98%</span>
          <span>•</span>
          <span>Showing {totalFields} extracted fields</span>
        </div>
      </CardHeader>

      <CardContent className="flex-1 overflow-hidden">
        {/* Tabs: Bring back Basic, Financial, Address, Technical, Other and add Identifier & Line Items */}
        <Tabs defaultValue="identifier" className="w-full h-full flex flex-col">
          <TabsList className="flex w-full flex-wrap gap-2 h-auto flex-shrink-0">
            <TabsTrigger value="basic" className="gap-1.5 text-xs">
              <FileText className="h-3 w-3" />
              Basic ({fieldGroups.basic.length})
            </TabsTrigger>
            <TabsTrigger value="financial" className="gap-1.5 text-xs">
              <Table2 className="h-3 w-3" />
              Financial ({fieldGroups.financial.length})
            </TabsTrigger>
            <TabsTrigger value="address" className="gap-1.5 text-xs">
              <FileText className="h-3 w-3" />
              Address ({fieldGroups.address.length})
            </TabsTrigger>
            <TabsTrigger value="identifier" className="gap-1.5 text-xs">
              <Table2 className="h-3 w-3" />
              Identifier ({identifiers?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="lineItems" className="gap-1.5 text-xs">
              <Table2 className="h-3 w-3" />
              Line Items ({flatLineItems?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="tax" className="gap-1.5 text-xs">
              <Table2 className="h-3 w-3" />
              Tax ({taxItems?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="technical" className="gap-1.5 text-xs">
              <Code className="h-3 w-3" />
              Technical ({fieldGroups.technical.length})
            </TabsTrigger>
            <TabsTrigger value="other" className="gap-1.5 text-xs">
              <AlertTriangle className="h-3 w-3" />
              Other ({fieldGroups.other.length})
            </TabsTrigger>
          </TabsList>

          {/* Basic Fields Tab */}
          <TabsContent value="basic" className="mt-3 flex-1 overflow-auto">
            <div className="space-y-2">
              {fieldGroups.basic.map(({ label, field, value }) => {
                const fieldConfidence = getFieldConfidence(field);
                const needsReview = header.needs_review.includes(field);
                const formattedValue = formatFieldValue(value);
                return (
                  <div key={field} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-md p-2.5 shadow-sm">
                    <div className="text-xs text-muted-foreground mb-1.5 flex items-center justify-between">
                      <span>{label}</span>
                      <ConfidenceIndicator confidence={fieldConfidence} size="sm" />
                    </div>
                    {needsReview ? (
                      <FieldEditor field={field} value={formattedValue} confidence={fieldConfidence} onSave={(nv) => onFieldUpdate(field, nv)} />
                    ) : (
                      <div className="font-medium text-sm break-words">
                        {formattedValue || <span className="text-muted-foreground italic">Not found</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Financial Fields Tab */}
          <TabsContent value="financial" className="mt-3 flex-1 overflow-auto">
            <div className="space-y-2">
              {fieldGroups.financial.map(({ label, field, value }) => {
                const fieldConfidence = getFieldConfidence(field);
                const needsReview = header.needs_review.includes(field);
                const formattedValue = formatFieldValue(value);
                return (
                  <div key={field} className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-md p-2.5 shadow-sm">
                    <div className="text-xs text-muted-foreground mb-1.5 flex items-center justify-between">
                      <span>{label}</span>
                      <ConfidenceIndicator confidence={fieldConfidence} size="sm" />
                    </div>
                    {needsReview ? (
                      <FieldEditor field={field} value={formattedValue} confidence={fieldConfidence} onSave={(nv) => onFieldUpdate(field, nv)} />
                    ) : (
                      <div className="font-medium text-sm break-words">
                        {formattedValue || <span className="text-muted-foreground italic">Not found</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Address Fields Tab */}
          <TabsContent value="address" className="mt-3 flex-1 overflow-auto">
            <div className="space-y-2">
              {fieldGroups.address.map(({ label, field, value }) => {
                const fieldConfidence = getFieldConfidence(field);
                const needsReview = header.needs_review.includes(field);
                const formattedValue = formatFieldValue(value);
                return (
                  <div key={field} className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-md p-2.5 shadow-sm">
                    <div className="text-xs text-muted-foreground mb-1.5 flex items-center justify-between">
                      <span>{label}</span>
                      <ConfidenceIndicator confidence={fieldConfidence} size="sm" />
                    </div>
                    {needsReview ? (
                      <FieldEditor field={field} value={formattedValue} confidence={fieldConfidence} onSave={(nv) => onFieldUpdate(field, nv)} />
                    ) : (
                      <div className="font-medium text-sm break-words">
                        {formattedValue || <span className="text-muted-foreground italic">Not found</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="identifier" className="mt-3 flex-1 overflow-auto">
            {(!identifiers || identifiers.length === 0) ? (
              <div className="text-sm text-muted-foreground">No identifier data found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0 z-10">
                    <tr>
                      <th className="text-left font-semibold p-2">Identifier</th>
                      <th className="text-left font-semibold p-2">Utility Type</th>
                      <th className="text-left font-semibold p-2">Period From</th>
                      <th className="text-left font-semibold p-2">Period To</th>
                      <th className="text-left font-semibold p-2">Read Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {identifiers.map((it: any, idx: number) => (
                      <tr key={idx} className="border-b hover:bg-slate-50">
                        <td className="p-2">{fmt(it?.identifier)}</td>
                        <td className="p-2">{fmt(it?.utilityType)}</td>
                        <td className="p-2">{fmt(it?.periodFrom)}</td>
                        <td className="p-2">{fmt(it?.periodTo)}</td>
                        <td className="p-2">{fmt(it?.readType)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="lineItems" className="mt-3 flex-1 overflow-auto">
            {(!flatLineItems || flatLineItems.length === 0) ? (
              <div className="text-sm text-muted-foreground">No line items found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0 z-10">
                    <tr>
                      <th className="text-left font-semibold p-2">Meter Number</th>
                      <th className="text-left font-semibold p-2">Multiplier</th>
                      <th className="text-left font-semibold p-2">Description</th>
                      <th className="text-left font-semibold p-2">Prev Reading</th>
                      <th className="text-left font-semibold p-2">Curr Reading</th>
                      <th className="text-left font-semibold p-2">Quantity</th>
                      <th className="text-left font-semibold p-2">Quantity UOM</th>
                      <th className="text-left font-semibold p-2">Rate</th>
                      <th className="text-left font-semibold p-2">Rate UOM</th>
                      <th className="text-left font-semibold p-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {flatLineItems.map((li: any, idx: number) => (
                      <tr key={idx} className="border-b hover:bg-slate-50">
                        <td className="p-2">{fmt(li?.meterNumber)}</td>
                        <td className="p-2">{fmt(li?.meterMultiplier)}</td>
                        <td className="p-2">{fmt(li?.lineItemDescription)}</td>
                        <td className="p-2">{fmt(li?.previousReading)}</td>
                        <td className="p-2">{fmt(li?.currentReading)}</td>
                        <td className="p-2">{fmt(li?.quantity)}</td>
                        <td className="p-2">{fmt(li?.quantityUOM)}</td>
                        <td className="p-2">{fmt(li?.rate)}</td>
                        <td className="p-2">{fmt(li?.rateUOM)}</td>
                        <td className="p-2">{fmt(li?.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Tax-only Tab (derived from Line Items) */}
          <TabsContent value="tax" className="mt-3 flex-1 overflow-auto">
            {(!taxItems || taxItems.length === 0) ? (
              <div className="text-sm text-muted-foreground">No tax items found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0 z-10">
                    <tr>
                      <th className="text-left font-semibold p-2">Tax Type</th>
                      <th className="text-left font-semibold p-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taxItems.map((li: any, idx: number) => (
                      <tr key={idx} className="border-b hover:bg-slate-50">
                        <td className="p-2">{fmt(li?.lineItemDescription)}</td>
                        <td className="p-2">{fmt(li?.total ?? li?.rate ?? '')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Technical Fields Tab */}
          <TabsContent value="technical" className="mt-3 flex-1 overflow-auto">
            <div className="space-y-2">
              {fieldGroups.technical.map(({ label, field, value }) => {
                const fieldConfidence = getFieldConfidence(field);
                const needsReview = header.needs_review.includes(field);
                const formattedValue = formatFieldValue(value);
                return (
                  <div key={field} className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-md p-2.5 shadow-sm">
                    <div className="text-xs text-muted-foreground mb-1.5 flex items-center justify-between">
                      <span>{label}</span>
                      <ConfidenceIndicator confidence={fieldConfidence} size="sm" />
                    </div>
                    {needsReview ? (
                      <FieldEditor field={field} value={formattedValue} confidence={fieldConfidence} onSave={(nv) => onFieldUpdate(field, nv)} />
                    ) : (
                      <div className="font-medium text-sm break-words">
                        {formattedValue || <span className="text-muted-foreground italic">Not found</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Other Fields Tab */}
          <TabsContent value="other" className="mt-3 flex-1 overflow-auto">
            <div className="space-y-2">
              {fieldGroups.other.map(({ label, field, value }) => {
                const fieldConfidence = getFieldConfidence(field);
                const needsReview = header.needs_review.includes(field);
                const formattedValue = formatFieldValue(value);
                return (
                  <div key={field} className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-md p-2.5 shadow-sm">
                    <div className="text-xs text-muted-foreground mb-1.5 flex items-center justify-between">
                      <span>{label}</span>
                      <ConfidenceIndicator confidence={fieldConfidence} size="sm" />
                    </div>
                    {needsReview ? (
                      <FieldEditor field={field} value={formattedValue} confidence={fieldConfidence} onSave={(nv) => onFieldUpdate(field, nv)} />
                    ) : (
                      <div className="font-medium text-sm break-words">
                        {formattedValue || <span className="text-muted-foreground italic">Not found</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
